/**
 * Dialog Service (confirm / alert) abstraction.
 *
 * Provides `confirmDialog(message, options)` and `alertDialog(message, options)`
 * so components open BootstrapVue message-box dialogs through a project-level
 * API instead of calling `$bvModal.msgBoxConfirm` / `$bvModal.msgBoxOk`
 * directly. Both methods return the same Promise the underlying `$bvModal`
 * helpers return (`msgBoxConfirm` → `Promise<boolean|null>`, `msgBoxOk` →
 * `Promise<true|null>`), so existing `.then(...)` / `await` flows are unchanged.
 *
 * Why this exists (Vue 3 forward-compatibility):
 *   `$bvModal.msgBoxConfirm()` / `$bvModal.msgBoxOk()` (the imperative message-box
 *   API) do not exist in bootstrap-vue-next, the Vue 3 successor — confirm/alert
 *   dialogs there are driven by `useModalController()` / an orchestrator instead.
 *   Routing every call site through these two methods means the eventual swap
 *   touches ONE file rather than every scattered `$bvModal.msgBox*` call. Mixins
 *   also map cleanly onto Vue 3 composables, so this can later become a
 *   `useDialog()` composable without changing call sites' intent.
 *
 * This is a thin, behavior-preserving pass-through: the `message` and `options`
 * are forwarded verbatim to `$bvModal`, with no injected defaults, so each call
 * site keeps its exact title/variant/button/layout options.
 *
 * @param {string|VNode|Array} message  Dialog body — string or rendered VNode(s).
 * @param {object} [options]            Raw BootstrapVue msgBox options
 *                                      (title, okVariant, okTitle, cancelTitle,
 *                                      footerClass, hideHeaderClose, html, …).
 * @returns {Promise} The Promise returned by the underlying `$bvModal` helper.
 */
export const DialogMixin = {
  methods: {
    confirmDialog(message, options = {}) {
      return this.$bvModal.msgBoxConfirm(message, options);
    },
    alertDialog(message, options = {}) {
      return this.$bvModal.msgBoxOk(message, options);
    }
  }
};

export default DialogMixin;
